import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { conversationsUtilsEndpoints } from '@/api/endpoints';
import { callPost } from '@/api/fetcher';
import { LogMessage } from '@/shared/types/analytics';
import { computeSessionBoundDate } from '@/util/dateUtils';
import { isUuid } from '@/util/util';

import { selectSessionStart } from '../redux/selectors';

export type UseSessionTranslationResult = {
  translated?: LogMessage[];
  isLoading: boolean;
  isError: boolean;
  status: 'pending' | 'success' | 'error';
  /** Imperative refresh — convenient when retrying after an error toast. */
  refetch: () => void;
};

/**
 * Translates the messages of a session into a target language via the
 * `translateLogs` REST endpoint. Cached by `(sessionId, language)` so flipping
 * the dropdown doesn't re-fetch the same combination twice.
 *
 * No side effects: no toast on completion,
 * no Redux dispatch. The owning component is responsible for surface-level UX.
 */
export const useSessionTranslation = (
  sessionId: string | undefined,
  language: string | undefined
): UseSessionTranslationResult => {
  const enabled = Boolean(sessionId) && Boolean(language) && isUuid(sessionId);
  const sessionStart = useSelector(selectSessionStart);
  const startDate = sessionStart ? computeSessionBoundDate(sessionStart) : undefined;

  const { data, isLoading, isError, status, refetch } = useQuery<LogMessage[]>({
    queryKey: ['translatedLogs', sessionId, language, startDate],
    queryFn: () =>
      callPost(conversationsUtilsEndpoints.translateLogs(sessionId!, language!, startDate), {}),
    enabled,
  });

  return {
    translated: data,
    isLoading,
    isError,
    status,
    refetch: () => {
      void refetch();
    },
  };
};
